import type { CheckoutCarRentalService } from '@/scopes/checkout/domain/types'
import type { FilterableCarItem } from '@/scopes/checkout/filterableCars/domain/types'
import { sub } from 'date-fns'
import { produce } from 'immer'
import { buildUniqOptionCarId } from '@/scopes/checkout/infrastructure/api/service-mappers/carRentalFilteringServiceMapper'

function formatDate(date: Date): string {
  const day = `0${date.getDate()}`.slice(-2)
  const month = `0${date.getMonth() + 1}`.slice(-2)
  const year = date.getFullYear()
  return `${year}-${month}-${day}`
}

function setParticipantBirthdate(
  service: CheckoutCarRentalService,
  participantId: string,
  birthDate: Date | null,
) {
  service.participants[participantId].birthdate = birthDate ? formatDate(birthDate) : null
}

function setMinAgeBirthdateIfNeeded(service: CheckoutCarRentalService, participantId: string) {
  if (
    service?.participants[participantId]
    && !service.participants[participantId].birthdate
    && service.configurations
  ) {
    const minAgeDate = sub(new Date(), { years: service.configurations.rentalConditionsMinAgeDriver })
    setParticipantBirthdate(service, participantId, minAgeDate)
  }
}

interface AddCarRentalServicePayload {
  participantId: string
  value: string
  id: string
  driverBirthDate: Date | null
  bookedEquipmentIds: string[]
  bookedEquipmentsWithQuantity?: { id: string, quantity: number }[]
}

export function addCar(payload: AddCarRentalServicePayload) {
  return produce((service: CheckoutCarRentalService) => {
    const optionId = buildUniqOptionCarId(payload.id, payload.value)
    service.carLists?.push({
      selectedValue: payload.value,
      selectedId: payload.id ?? '',
      driverId: payload.participantId,
      availableOptionsToUpgrade: [],
    })
    setParticipantBirthdate(service, payload.participantId, payload.driverBirthDate)
    service.options[optionId].driverId = payload.participantId
    service.options[optionId].selectedEquipments = payload.bookedEquipmentIds
  })
}

export function editCar(payload: AddCarRentalServicePayload) {
  return produce((service: CheckoutCarRentalService) => {
    const optionId = buildUniqOptionCarId(payload.id, payload.value)
    service.carLists = service.carLists?.map(carList =>
      carList.selectedValue !== payload.value
        ? carList
        : {
            ...carList,
            selectedValue: payload.value,
            selectedId: payload.id ?? '',
            driverId: payload.participantId,
            availableOptionsToUpgrade: [],
          },
    ) ?? []
    setParticipantBirthdate(service, payload.participantId, payload.driverBirthDate)
    service.options[optionId].driverId = payload.participantId
    service.options[optionId].selectedEquipments = payload.bookedEquipmentIds
    service.options[optionId].selectedEquipmentsWithQuantity = payload.bookedEquipmentsWithQuantity ?? []
  })
}

interface RemoveCarRentalServicePayload {
  participantId: string
}

export function removeCar(payload: RemoveCarRentalServicePayload) {
  return produce((service: CheckoutCarRentalService) => {
    const previousSelectedValue = service.participants[payload.participantId].selectedValue
    const previousSelectedId = service.participants[payload.participantId].selectedId
    const previousSelectedOptionId = buildUniqOptionCarId(previousSelectedId ?? '', previousSelectedValue ?? '')
    if (!previousSelectedValue)
      return
    service.carLists = service.carLists?.filter(car => car.driverId !== payload.participantId) ?? []
    service.options[previousSelectedOptionId].driverId = null
    service.options[previousSelectedOptionId].selectedEquipments = []
  })
}

interface UpgradeCarRentalServicePayload {
  participantId: string
  value: string
  id: string
}

export function upgradeCar(payload: UpgradeCarRentalServicePayload) {
  return produce((service: CheckoutCarRentalService) => {
    const previousSelectedValue = service.participants[payload.participantId].selectedValue
    const previousSelectedId = service.participants[payload.participantId].selectedId
    const previousSelectedOptionId = buildUniqOptionCarId(previousSelectedId ?? '', previousSelectedValue ?? '')
    const optionId = buildUniqOptionCarId(payload.id, payload.value)
    if (!previousSelectedValue)
      return
    setMinAgeBirthdateIfNeeded(service, payload.participantId)
    service.carLists = service.carLists?.map(car =>
      car.driverId === payload.participantId
        ? { ...car, selectedValue: payload.value, selectedId: payload.id ?? '' }
        : car,
    ) ?? []
    service.options[optionId].driverId = payload.participantId
    service.options[previousSelectedOptionId].selectedEquipments = []
    service.options[previousSelectedOptionId].driverId = null
  })
}

export function addCarToOptionAndUpgradeCar(payload: UpgradeCarRentalServicePayload) {
  return produce((service: CheckoutCarRentalService, cars: Record<string, FilterableCarItem>) => {
    const previousSelectedValue = service.participants[payload.participantId].selectedValue
    const previousSelectedId = service.participants[payload.participantId].selectedId
    const previousSelectedOptionId = buildUniqOptionCarId(previousSelectedId ?? '', previousSelectedValue ?? '')
    const optionId = buildUniqOptionCarId(payload.id, payload.value)
    if (!previousSelectedValue)
      return
    setMinAgeBirthdateIfNeeded(service, payload.participantId)
    service.carLists = service.carLists?.map(car =>
      car.driverId === payload.participantId
        ? { ...car, selectedValue: payload.value, selectedId: payload.id ?? '' }
        : car,
    ) ?? []
    if (!service.options[optionId]) {
      service.options[optionId] = {
        ...cars[payload.id ?? ''],
        driverId: payload.participantId,
        selectedEquipments: [],
        selectedEquipmentsWithQuantity: [],
        availableOnRequest: false,
        maxNumberOfEquipments: 3,
        openJawFeeAmount: 0,
        location: { text: '', icon: 'CarRight' },
        equipments: [],
      }
    }
    service.options[optionId].driverId = payload.participantId
    service.options[previousSelectedOptionId].selectedEquipments = []
    service.options[previousSelectedOptionId].driverId = null
  })
}
